import { SurveyModuleInfo, SurveyTemplate } from "@allaihealth/survey";
import { firstValueFrom, Subscription } from "rxjs";
import { RuntimeLoadModuleTrigger } from "./runtime-load-module.trigger";
import { SurveyService } from "../services/survey.service";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { SurveyState } from "./survey-state";

/**
 * <p>Utility for loading / unloading modules from {@link RuntimeLoadModuleTrigger}'s. This is done in a slightly
 * complicated way by listening to a subject on the trigger and making the changes once all triggers have fired. This
 * is why there needs to be a 0 time {@link setTimeout} to wait for the next run through. That way multiple triggers
 * can be accounted for, but if the user changes their answers they won't conflict with each other.</p>
 * <p>Modules that are loaded are cached so that subsequent loads of the same module won't cause additional loads.</p>
 */
export class ModuleLoader {
	/** Modules that have been fully loaded */
	private loadedModules:Map<string, SurveyModuleInfo> = new Map();

	/** Modules that are in the process of loading */
	private loadingModules:Map<string, Promise<SurveyModuleInfo>> = new Map();

	/** Subscription from the triggers when they fire */
	private subscription:Subscription;

	/** Pending changes that need to be made to the active survey */
	private pendingChanges:RuntimeLoadModuleTrigger[];

	/**
	 * Are we currently in a load cycle? If false, then we are in a new cycle and triggers should be considered from a
	 * different cycle and pending changes should be thrown out
	 */
	private loading:boolean = false;

	constructor(private http:HttpClient, private surveyService:SurveyService) {
		this.subscription = this.surveyService.onUnloadLoadModule.subscribe((trigger) =>
			this.handleTrigger(trigger));
	}

	/** Call to dispose listening to triggers */
	dispose() {
		this.subscription?.unsubscribe();
	}

	/** Called when a new trigger has activated */
	private handleTrigger(trigger:RuntimeLoadModuleTrigger) {
		//if we are not in a change cycle then start over with changes
		if(!this.loading) {
			console.debug(`Change cycle started`);

			this.pendingChanges = [];

			this.loading = true;

			//set a timeout for the current cycle to make sure time has passed before the next cycle
			setTimeout(() => {
				this.loading = false;
				console.debug(`Change cycle completed with ${this.pendingChanges.length} pending changes`);
			});
		}

		// add a loading module only if it hasn't already be loaded yet
		if(trigger.isTriggered && !this.hasModule(trigger.moduleId)) {
			this.loadingModules.set(trigger.moduleId, this.doLoadModule(trigger.moduleId));
		}

		this.pendingChanges.push(trigger);
	}

	/** Preform a load of a module from the backend */
	private async doLoadModule(moduleId:string) {
		const template =
			await firstValueFrom(this.http.get<SurveyTemplate>(`${environment.apiUrl}/module/${moduleId}`));

		const newModule:SurveyModuleInfo = {
			moduleId,
			survey: template
		};

		//once finished add to the already loaded modules
		this.loadedModules.set(moduleId, newModule);

		//remove from the modules being loaded
		this.loadingModules.delete(moduleId);

		return newModule;
	}

	/** Is a module already loading or loaded? */
	hasModule(moduleId:string) {
		return this.loadedModules.has(moduleId) || this.loadingModules.has(moduleId);
	}

	/**
	 * Get a list of pending changes to make to the current survey. If modules are still loading this will wait until
	 * they are complete.
	 */
	async getPendingChanges():Promise<PendingChanges> {
		if(this.loadingModules.size > 0) {
			await Promise.all(this.loadingModules.values());
		}

		const pendingChanges = new PendingChanges();

		//go through each change and create the pending changes to pass off to the state
		for(const change of this.pendingChanges) {
			if(change.isTriggered) {
				pendingChanges.toLoad.push({
					...this.loadedModules.get(change.moduleId),
					sourceId: change.sourceId
				});
			} else {
				pendingChanges.toUnload.add(change.moduleId);
			}
		}

		this.pendingChanges = [];

		return pendingChanges;
	}

	/**
	 * Are there pending changes to be made to the state?
	 * @param state the current state of the survey to check if there are changes
	 */
	hasPendingChanges(state:SurveyState) {
		if(!this.pendingChanges || this.pendingChanges.length == 0) {
			return false;
		}

		for(const change of this.pendingChanges) {
			if(change.isTriggered != state.hasModule(change.moduleId)) {
				return true;
			}
		}

		return false;
	}
}

/** Lists of modules to load/unload */
export class PendingChanges {
	toUnload:Set<string> = new Set();
	toLoad:PendingLoad[] = [];
}

/** Module that needs to be loaded */
export interface PendingLoad extends SurveyModuleInfo {
	/** The source module that this load request came from */
	sourceId:string;
}
